<template>
  <BlBackdrop
    title="Edit problem behavior"
    label="Update"
    :submitLoading="submitLoading"
    :disabled="isDisabledSubmit"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onSubmit"
  >
    <div
      class="flex flex-col gap-4 px-8 pt-5 overflow-scroll"
      :style="{ width: '40rem', maxHeight: 'calc(100vh - 16rem)' }"
    >
      <div v-for="item in defaultProblemBehaviors" :key="item.id">
        <div class="flex items-center justify-between gap-2 pb-3">
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded"
              :style="{
                backgroundColor: item.color || '#2AB1A8'
              }"
            />
            <BlTextBody2
              :text="`${item.code} - ${item.code_definition}`"
              class="text-slate-1000"
            />
          </div>
          <div class="" @click="deleteProblemBehavior(item)">
            <BlIcon
              :icon="!item.is_used ? 'delete' : 'delete'"
              :color="!item.is_used ? 'error' : 'grey'"
              class="w-5 h-5 cursor-pointer"
              tooltip-position="bottom-left"
              :tooltip="
                item.is_used
                  ? 'This behavior has recorded data and cannot be deleted.'
                  : ''
              "
            />
          </div>
        </div>
        <div class="">
          <BlInputText
            :input="getDefaultDescriptionInput(item)"
            @changed="payload => updateDefaultDescription(payload, item.id)"
          />
        </div>
      </div>
      <div
        v-for="newProblemBehavior in newProblemBehaviors"
        :key="newProblemBehavior.key"
      >
        <div class="flex flex-col gap-2 pb-3 border-b">
          <div class="flex items-center w-full gap-4">
            <BlSelectBox
              :options="getFilteredCodeOptions(newProblemBehavior.key)"
              class="flex-shrink-0 w-32"
              v-model="problemBehaviorCodeSelections[newProblemBehavior.key]"
              @change="value => onChangeCode(value, newProblemBehavior.key)"
              placeholder="Code"
              :key="
                `select-${newProblemBehavior.key}-${selectBoxKeys[
                  newProblemBehavior.key
                ] || 0}`
              "
            />
            <BlInputText
              :input="
                getProblemBehaviorInput(newProblemBehavior.key)
                  .codeDefinitionInput
              "
              class="w-full"
              @changed="
                payload =>
                  inputHandler(
                    payload,
                    newProblemBehavior.key,
                    'codeDefinition'
                  )
              "
            />
            <div
              class="relative flex items-center justify-between flex-shrink-0 px-3 border rounded cursor-pointer w-13 bg-pure-white border-grey-200 h-11"
            >
              <div
                class="w-full h-6"
                :style="{
                  backgroundColor: newProblemBehavior.color || '#DD3F30',
                  borderRadius: '2px'
                }"
              />
            </div>
            <div
              class=""
              @click="deleteNewProblemBehavior(newProblemBehavior.key)"
            >
              <BlIcon
                icon="delete"
                color="error"
                class="w-5 h-5 cursor-pointer"
              />
            </div>
          </div>
          <div class="flex items-start gap-2">
            <BlInputText
              :input="
                getProblemBehaviorInput(newProblemBehavior.key).descriptionInput
              "
              @changed="
                payload =>
                  inputHandler(payload, newProblemBehavior.key, 'description')
              "
            />
          </div>
        </div>
      </div>
      <BlInputButton
        v-if="!allCodesUsed"
        label="Add problem behavior"
        icon="plus"
        class="self-start px-0"
        type="plain"
        @clicked="onAddProblemBehavior"
      />
      <BlTooltip
        v-if="allCodesUsed"
        style="width: fit-content;"
        drop="top"
        tooltip="You've reached the limit for problem behaviors and can't create a new one."
      >
        <BlInputButton
          label="Add problem behavior"
          icon="plus"
          class="self-start px-2 cursor-not-allowed"
          type="plain"
          disabled
        />
      </BlTooltip>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import BlBackdrop from "../../../BlBackdrop.vue";
import BlIcon from "../../../BlIcon.vue";
import BlTextTitle4 from "../../../BlTextTitle4.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlSelectBox from "../../../BlSelectBox.vue";
import BlInputText from "../../../BlInputText.vue";
import BlTooltip from "../../../BlTooltip.vue";

export default {
  name: "EditTargetBehaviorDetailsModal",
  components: {
    BlBackdrop,
    BlIcon,
    BlTextTitle4,
    BlTextBody2,
    BlInputButton,
    BlSelectBox,
    BlInputText,
    BlTooltip
  },
  props: ["targetProblemBehaviors", "target"],
  data() {
    return {
      submitLoading: false,
      defaultProblemBehaviors: [],
      newProblemBehaviors: [],
      softDeleteProblemBehaviors: [],
      loadingProblemBehaviors: false,
      problemBehaviorCodeSelections: {},
      problemBehaviorsInputs: {},
      defaultDescriptionInputs: {},
      selectBoxKeys: {}
    };
  },
  computed: {
    ...mapGetters(["problemBehaviors"]),
    isDisabledSubmit() {
      if (this.newProblemBehaviors.length > 0) {
        return this.newProblemBehaviors.some(behavior => {
          const codeSelection = this.problemBehaviorCodeSelections[behavior.key];
          return !codeSelection || codeSelection === "";
        });
      }
      return false;
    },
    codeOptions() {
      if (!this.problemBehaviors || !this.problemBehaviors.length) {
        return [];
      }
      return this.problemBehaviors.map(i => {
        // Check if this code is already used
        const isDisabled = this.isCodeAlreadyUsed(i.id);
        return {
          value: i.id,
          label: i.code,
          disabled: isDisabled
        };
      });
    },
    usedCodeList() {
      const defaultBehaviorCodes = this.defaultProblemBehaviors
        .map(behavior => {
          const codeObj = this.problemBehaviors.find(
            c => c.id === behavior.problem_behavior_id || c.id === behavior.id
          );
          return codeObj ? codeObj.code.toUpperCase() : "";
        })
        .filter(code => code);

      const newBehaviorCodes = Object.keys(this.problemBehaviorCodeSelections)
        .filter(key => this.problemBehaviorCodeSelections[key])
        .map(key => {
          const codeId = this.problemBehaviorCodeSelections[key];
          const codeObj = this.problemBehaviors.find(c => c.id === codeId);
          return codeObj ? codeObj.code.toUpperCase() : "";
        })
        .filter(code => code);

      const softDeletedCodes = this.softDeleteProblemBehaviors
        .map(behavior => {
          const codeObj = this.problemBehaviors.find(
            c => c.id === behavior.problem_behavior_id || c.id === behavior.id
          );
          return codeObj ? codeObj.code.toUpperCase() : "";
        })
        .filter(code => code);

      return [
        ...new Set([...defaultBehaviorCodes, ...newBehaviorCodes])
      ].filter(code => !softDeletedCodes.includes(code));
    },

    allCodesUsed() {
      const totalBehaviorsUsed =
        this.defaultProblemBehaviors.length + this.newProblemBehaviors.length;
      return totalBehaviorsUsed >= this.problemBehaviors.length;
    }
  },
  mounted() {
    this.fetchProblemBehaviors();
    this.initializeDefaultProblemBehaviors();
    if (this.problemBehaviors && this.problemBehaviors.length) {
      this.problemBehaviors.forEach(task => {
        this.$set(this.selectBoxKeys, task.id, 0);
      });
    }
    this.sortTasksByPosition();
  },
  methods: {
    ...mapActions(["showToast", "getProblemBehaviors", "updateTarget"]),

    getCurrentUsedCodes() {
      const defaultBehaviorCodes = this.defaultProblemBehaviors
        .map(behavior => {
          const codeObj = this.problemBehaviors.find(
            c => c.id === behavior.problem_behavior_id || c.id === behavior.id
          );
          return codeObj ? codeObj.code.toUpperCase() : "";
        })
        .filter(code => code);

      const newBehaviorCodes = [];
      for (const key in this.problemBehaviorCodeSelections) {
        const codeId = this.problemBehaviorCodeSelections[key];
        if (codeId) {
          const codeObj = this.problemBehaviors.find(c => c.id === codeId);
          if (codeObj) {
            newBehaviorCodes.push(codeObj.code.toUpperCase());
          }
        }
      }

      const deletedCodes = this.softDeleteProblemBehaviors
        .map(behavior => {
          const codeObj = this.problemBehaviors.find(
            c => c.id === behavior.problem_behavior_id || c.id === behavior.id
          );
          return codeObj ? codeObj.code.toUpperCase() : "";
        })
        .filter(code => code);

      const allCodes = [
        ...new Set([...defaultBehaviorCodes, ...newBehaviorCodes])
      ];
      return allCodes.filter(code => !deletedCodes.includes(code));
    },

    getFilteredCodeOptions(currentBehaviorKey) {
      if (!this.problemBehaviors || !this.problemBehaviors.length) {
        return [];
      }

      let currentCode = "";
      const currentSelection = this.problemBehaviorCodeSelections[currentBehaviorKey];
      if (currentSelection) {
        const codeObj = this.problemBehaviors.find(
          c => c.id === currentSelection
        );
        if (codeObj) {
          currentCode = codeObj.code.toUpperCase();
        }
      }

      const usedCodes = [];

      this.defaultProblemBehaviors.forEach(behavior => {
        const codeObj = this.problemBehaviors.find(
          c => c.id === behavior.problem_behavior_id || c.id === behavior.id
        );
        if (codeObj) usedCodes.push(codeObj.code.toUpperCase());
      });

      Object.keys(this.problemBehaviorCodeSelections).forEach(key => {
        if (
          key !== currentBehaviorKey &&
          this.problemBehaviorCodeSelections[key]
        ) {
          const codeObj = this.problemBehaviors.find(
            c => c.id === this.problemBehaviorCodeSelections[key]
          );
          if (codeObj) usedCodes.push(codeObj.code.toUpperCase());
        }
      });

      return this.problemBehaviors.map(codeObj => {
        const codeUpperCase = codeObj.code.toUpperCase();
        const isUsed =
          usedCodes.includes(codeUpperCase) && codeUpperCase !== currentCode;

        return {
          value: codeObj.id,
          label: codeObj.code,
          disabled: isUsed
        };
      });
    },
    async fetchProblemBehaviors() {
      this.loadingProblemBehaviors = true;
      await this.getProblemBehaviors();
      this.loadingProblemBehaviors = false;
    },

    initializeDefaultProblemBehaviors() {
      this.defaultProblemBehaviors = [...this.targetProblemBehaviors];

      this.defaultProblemBehaviors.forEach(item => {
        this.$set(this.defaultDescriptionInputs, item.id, {
          placeholder: "Type description",
          model: item.description || "",
          textarea: true
        });
      });
    },

    isCodeAlreadyUsed(codeId, excludeTaskKey = null) {
      const isUsedInDefaultTasks = this.defaultProblemBehaviors.some(
        pb => pb.problem_behavior_id === codeId || pb.id === codeId
      );

      const isUsedInNewTasks = this.newProblemBehaviors.some(
        pb => pb.id === codeId && (!excludeTaskKey || pb.key !== excludeTaskKey)
      );

      return isUsedInDefaultTasks || isUsedInNewTasks;
    },

    getDefaultDescriptionInput(item) {
      if (!this.defaultDescriptionInputs[item.id]) {
        this.$set(this.defaultDescriptionInputs, item.id, {
          placeholder: "Type description",
          model: item.description || "",
          textarea: true
        });
      }
      return this.defaultDescriptionInputs[item.id];
    },

    updateDefaultDescription(payload, itemId) {
      const index = this.defaultProblemBehaviors.findIndex(
        item => item.id === itemId
      );
      if (index > -1) {
        const updatedItem = {
          ...this.defaultProblemBehaviors[index],
          description: payload.value
        };
        this.$set(this.defaultProblemBehaviors, index, updatedItem);

        // Update the input model as well
        if (this.defaultDescriptionInputs[itemId]) {
          this.defaultDescriptionInputs[itemId].model = payload.value;
        }
      }
    },

    getProblemBehaviorInput(key) {
      if (!this.problemBehaviorsInputs[key]) {
        this.$set(this.problemBehaviorsInputs, key, {
          codeDefinitionInput: {
            placeholder: "Type code definition",
            model: ""
          },
          descriptionInput: {
            placeholder: "Type description",
            model: "",
            textarea: true
          }
        });
      }
      return this.problemBehaviorsInputs[key];
    },

    inputHandler(payload, problemBehaviorKey, inputType) {
      const problemBehaviorInput = this.getProblemBehaviorInput(
        problemBehaviorKey
      );

      if (inputType === "codeDefinition") {
        problemBehaviorInput.codeDefinitionInput.model = payload.value;
      }
      if (inputType === "description") {
        problemBehaviorInput.descriptionInput.model = payload.value;
      }

      this.updateProblemBehaviorFromInputs(problemBehaviorKey);
    },

    updateProblemBehaviorFromInputs(problemBehaviorKey) {
      const problemBehaviorIndex = this.newProblemBehaviors.findIndex(
        behavior => behavior.key === problemBehaviorKey
      );
      if (problemBehaviorIndex > -1) {
        const problemBehaviorInput = this.getProblemBehaviorInput(
          problemBehaviorKey
        );
        const updatedProblemBehavior = {
          ...this.newProblemBehaviors[problemBehaviorIndex],
          code_definition: problemBehaviorInput.codeDefinitionInput.model,
          description: problemBehaviorInput.descriptionInput.model
        };

        this.onUpdate(updatedProblemBehavior);
      }
    },

    onAddProblemBehavior() {
      if (this.allTasksUsed) {
        this.showToast({
          title: "Maximum Tasks Reached",
          message:
            "You've reached the limit for tasks and can't create a new one.",
          type: "error"
        });
        return;
      }

      let length = 0;
      if (this.defaultProblemBehaviors && this.defaultProblemBehaviors.length) {
        if (this.newProblemBehaviors && this.newProblemBehaviors.length) {
          const maxPosition = Math.max(
            ...this.newProblemBehaviors.map(pb => pb.position)
          );
          if (
            maxPosition >
            this.defaultProblemBehaviors.length +
              this.newProblemBehaviors.length
          ) {
            length = maxPosition;
          } else {
            length =
              this.defaultProblemBehaviors.length +
              this.newProblemBehaviors.length;
          }
        } else {
          length = this.defaultProblemBehaviors.length;
        }
      }

      function getRandomId() {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let combination = "";
        for (let i = 0; i < 6; i++) {
          combination += chars[Math.round(Math.random() * 35)];
        }
        return `problem-behavior-${combination}`;
      }

      const problemBehaviorKey = getRandomId();
      const newProblemBehavior = {
        key: problemBehaviorKey,
        id: 0,
        code: "",
        code_definition: "",
        description: "",
        color: "#DD3F30",
        position: length + 1
      };

      this.$set(this.problemBehaviorCodeSelections, problemBehaviorKey, "");
      this.$set(this.selectBoxKeys, problemBehaviorKey, 0);
      this.$set(this.problemBehaviorsInputs, problemBehaviorKey, {
        codeDefinitionInput: {
          placeholder: "Type code definition",
          model: ""
        },
        descriptionInput: {
          placeholder: "Type description",
          model: "",
          textarea: true
        }
      });

      const newArr = [...this.newProblemBehaviors];
      newArr.push(newProblemBehavior);
      this.newProblemBehaviors = newArr;
      this.sortTasksByPosition();
    },

    onChangeCode(selectedValue, behaviorKey) {
      if (!selectedValue) return;

      const findCode = this.problemBehaviors.find(i => i.id === selectedValue);
      if (!findCode) return;

      let currentCode = "";
      const currentSelection = this.problemBehaviorCodeSelections[behaviorKey];
      if (currentSelection) {
        const codeObj = this.problemBehaviors.find(
          c => c.id === currentSelection
        );
        if (codeObj) {
          currentCode = codeObj.code.toUpperCase();
        }
      }

      const usedCodes = this.getCurrentUsedCodes().filter(
        code => code !== currentCode
      );

      if (usedCodes.includes(findCode.code.toUpperCase())) {
        this.showToast({
          title: "Duplicate Code",
          message: `Code ${findCode.code} is already in use. Please select a different code.`,
          type: "error"
        });

        this.$set(this.problemBehaviorCodeSelections, behaviorKey, "");
        this.$set(
          this.selectBoxKeys,
          behaviorKey,
          (this.selectBoxKeys[behaviorKey] || 0) + 1
        );
        return;
      }

      this.$set(this.problemBehaviorCodeSelections, behaviorKey, selectedValue);

      const problemBehaviorIndex = this.newProblemBehaviors.findIndex(
        behavior => behavior.key === behaviorKey
      );
      if (problemBehaviorIndex > -1) {
        const updatedProblemBehavior = {
          ...this.newProblemBehaviors[problemBehaviorIndex],
          id: findCode.id,
          code: findCode.code,
          code_definition: findCode.code_definition,
          color: findCode.color
        };

        this.$set(
          this.newProblemBehaviors,
          problemBehaviorIndex,
          updatedProblemBehavior
        );

        const problemBehaviorInput = this.getProblemBehaviorInput(behaviorKey);
        problemBehaviorInput.codeDefinitionInput.model =
          findCode.code_definition;
        problemBehaviorInput.descriptionInput.model =
          findCode.description || "";

        this.onUpdate(updatedProblemBehavior);
        this.sortTasksByPosition();

        this.$nextTick(() => {
          Object.keys(this.selectBoxKeys).forEach(key => {
            if (key !== behaviorKey) {
              this.$set(
                this.selectBoxKeys,
                key,
                (this.selectBoxKeys[key] || 0) + 1
              );
            }
          });
        });
      }
    },
    onUpdate(updatedProblemBehavior) {
      const index = this.newProblemBehaviors.findIndex(
        i => i.key === updatedProblemBehavior.key
      );
      if (index > -1) {
        this.$set(this.newProblemBehaviors, index, updatedProblemBehavior);
      }
    },

    deleteProblemBehavior(problemBehavior) {
      if (problemBehavior.is_used) return;

      const index = this.defaultProblemBehaviors.findIndex(
        item => item.id === problemBehavior.id
      );
      if (index > -1) {
        this.defaultProblemBehaviors.splice(index, 1);
      }

      this.softDeleteProblemBehaviors.push({
        id: problemBehavior.id,
        _destroy: "1"
      });

      // Force update all dropdowns to reflect newly available codes
      this.$nextTick(() => {
        Object.keys(this.selectBoxKeys).forEach(key => {
          this.$set(
            this.selectBoxKeys,
            key,
            (this.selectBoxKeys[key] || 0) + 1
          );
        });
      });
      this.sortTasksByPosition();
    },
    sortTasksByPosition() {
      this.defaultProblemBehaviors.sort((a, b) => a.position - b.position);

      this.newProblemBehaviors.sort((a, b) => a.position - b.position);
    },

    deleteNewProblemBehavior(key) {
      const index = this.newProblemBehaviors.findIndex(
        problemBehavior => problemBehavior.key === key
      );
      if (index > -1) {
        this.newProblemBehaviors.splice(index, 1);

        this.$delete(this.problemBehaviorCodeSelections, key);
        this.$delete(this.problemBehaviorsInputs, key);
        this.$delete(this.selectBoxKeys, key);

        // Force update all remaining dropdowns
        this.$nextTick(() => {
          Object.keys(this.selectBoxKeys).forEach(key => {
            this.$set(
              this.selectBoxKeys,
              key,
              (this.selectBoxKeys[key] || 0) + 1
            );
          });
        });
        this.sortTasksByPosition();
      }
    },

    async onSubmit() {
      // Prepare the problem behaviors for submission
      const defaultBehaviors = this.defaultProblemBehaviors.map(item => {
        return {
          id: item.id,
          problem_behavior_id: item.problem_behavior_id,
          description: item.description
        };
      });

      const newBehaviors = this.newProblemBehaviors.map(item => {
        return {
          problem_behavior_id: item.id,
          description: item.description
        };
      });

      const params = {
        id: this.target.id,
        target: {
          target_problem_behaviors_attributes: [
            ...defaultBehaviors,
            ...newBehaviors,
            ...this.softDeleteProblemBehaviors
          ]
        }
      };

      this.submitLoading = true;
      const { success } = await this.updateTarget(params);
      this.submitLoading = false;
      if (!success) return;

      const title = "Task updated";
      const message = "Success! The task has been updated.";
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/modal/EditTargetBehaviorDetailsModal.vue